"use client"

import {
  JobsProvider,
  useJobsContext,
} from "@/app/[locale]/career/jobs/JobsContext"
import SearchIcon from "@mui/icons-material/Search"
import { CircularProgress, InputAdornment } from "@mui/material"
import { useTranslations } from "next-intl"
import { useParams, useRouter } from "next/navigation"
import React, { useState } from "react"
import { useInView } from "react-intersection-observer"
import {
  useInViewConfig,
  useIsDesktop,
  useIsLargeDesktop,
  useIsTabletOnly,
  useTransitionUpStyles,
} from "ui"
import SwiperCardBoxesContent from "ui/common/SwiperCardBoxesContent"
import { getNonUmbracoUrl } from "utils"
import { extractBlockProperties } from "utils/simplifyData"
import {
  SearchContainer,
  SearchIconWrapper,
  StyledAutocomplete,
  StyledAutocompletePaper,
  StyledBlockWrapper,
  StyledCommonTitle,
  StyledContainer,
  StyledOptionLink,
  StyledTextField,
} from "./styles"

interface CardsSearchProps {
  data: {
    title?: {
      value: string
    }
    text?: {
      value: string
    }
    searchText?: {
      value: string
    }
    itemsList?: any
    id?: string
  }
}

const CardsSearchContent: React.FC<CardsSearchProps> = ({ data }) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)
  const { id, title, text, searchText, itemsList } = data

  const router = useRouter()
  const params = useParams()
  const locale = params?.locale as string
  const [inputValue, setInputValue] = useState("")
  const jobsSearchT = useTranslations("JobsSearch")
  const cardsSearchT = useTranslations("CardsSearch")
  const isDesktop = useIsDesktop()
  const isLargeDesktop = useIsLargeDesktop()
  const isTablet = useIsTabletOnly()
  const { searchJobs, isLoading, searchQuery, allJobs } = useJobsContext()

  // Count jobs by profession name and update parsedItemsList with job counts
  const parsedItemsList = React.useMemo(() => {
    const baseItems = extractBlockProperties(itemsList?.blocks) || []

    // Count jobs by profession name
    const jobCountsByProfession = allJobs.reduce(
      (acc: Record<string, number>, job) => {
        if (job.professionName) {
          acc[job.professionName] = (acc[job.professionName] || 0) + 1
        }
        return acc
      },
      {},
    )

    // Get total job count
    const totalJobCount = allJobs.length

    // Update items with job counts based on matching profession names
    return baseItems.map((item: any) => {
      const itemTitle = item.title?.value || item.title || ""

      // Check if this is an "All Jobs" item (supports multiple variations)
      const isAllJobsItem =
        itemTitle.includes("כל המשרות") ||
        itemTitle.includes("כל משרות") ||
        itemTitle.toLowerCase().includes("all jobs") ||
        (itemTitle.includes("משרות") &&
          (itemTitle.includes("כל") || itemTitle.includes("all")))

      if (isAllJobsItem) {
        return {
          ...item,
          text:
            totalJobCount > 0
              ? cardsSearchT("jobsCount", { count: totalJobCount })
              : cardsSearchT("availableJobs"),
        }
      }

      // For specific profession items
      const jobCount = jobCountsByProfession[itemTitle] || 0

      return {
        ...item,
        text:
          jobCount > 0
            ? cardsSearchT("jobsCount", { count: jobCount })
            : cardsSearchT("availableJobs"),
      }
    })
  }, [itemsList?.blocks, allJobs])

  // Extract job titles for autocomplete suggestions
  const autocompleteOptions = React.useMemo(() => {
    if (!allJobs || allJobs.length === 0) return []

    // Create a unique list of job titles or other relevant job properties for suggestions
    return Array.from(new Set(allJobs.map((job) => job.title || ""))).filter(
      Boolean,
    )
  }, [allJobs])

  // Handle autocomplete option selection
  const handleAutocompleteChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: unknown,
    reason: string,
    details?: unknown,
  ) => {
    const selectedValue = newValue as string | null
    if (selectedValue !== null) {
      searchJobs(selectedValue)
      // Navigate to the all jobs page with the selected job title as query parameter
      router.push(
        `/${locale}/career/jobs/all-jobs/?title=${encodeURIComponent(selectedValue)}`,
      )
    }
  }

  // Handle input value changes for the Autocomplete
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    setInputValue(newInputValue)
    searchJobs(newInputValue)
  }

  // Handle card click for navigation if needed
  const handleCardClick = (item: any) => {
    // Check for various link properties that might exist on the item
    let navigationUrl = null

    // Check for direct link property
    if (item.link?.url) {
      navigationUrl = item.link.url
    } else if (item.link?.href) {
      navigationUrl = item.link.href
    } else if (item.link?.links?.[0]?.url) {
      navigationUrl = item.link.links[0].url
    }
    // Check for direct url property
    else if (item.url) {
      navigationUrl = item.url
    }

    // Navigate if we found a URL
    if (navigationUrl) {
      const cleanUrl = getNonUmbracoUrl(navigationUrl)
      window.location.href = `/${locale}${cleanUrl}`
    }
  }

  return (
    <StyledBlockWrapper id={id}>
      <StyledContainer ref={ref} style={transitionStyles}>
        <StyledCommonTitle text={text}>{title?.value}</StyledCommonTitle>
        <SearchContainer>
          <StyledAutocomplete
            freeSolo
            options={autocompleteOptions}
            value={searchQuery || null}
            onChange={handleAutocompleteChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            loading={isLoading}
            loadingText={jobsSearchT("loading")}
            noOptionsText={jobsSearchT("noResults")}
            PaperComponent={(props) => (
              <div {...props} style={StyledAutocompletePaper} />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option as string}>
                <StyledOptionLink
                  href={`/${locale}/career/jobs/all-jobs/?title=${encodeURIComponent(option as string)}`}
                >
                  {option as string}
                </StyledOptionLink>
              </li>
            )}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                placeholder={searchText?.value || jobsSearchT("placeholder")}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="start">
                      {isLoading ? (
                        <SearchIconWrapper>
                          <CircularProgress size={24} />
                        </SearchIconWrapper>
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </SearchContainer>

        {parsedItemsList && parsedItemsList?.length > 0 && (
          <SwiperCardBoxesContent
            itemsList={parsedItemsList}
            showArrowsNavigation={parsedItemsList.length > 4}
            desktopSlidesPerView={
              isTablet
                ? 2 // Tablet: half of desktop slides
                : isLargeDesktop
                  ? 7
                  : isDesktop
                    ? 4 // Desktop
                    : 1 // Mobile
            }
            onItemClick={handleCardClick}
          />
        )}
      </StyledContainer>
    </StyledBlockWrapper>
  )
}

// Wrap the component in a JobsProvider to ensure context availability
const CardsSearch: React.FC<CardsSearchProps> = (props) => {
  return (
    <JobsProvider culture="he-il">
      <CardsSearchContent {...props} />
    </JobsProvider>
  )
}

export default CardsSearch
